import gsap, { SplitText } from "@lib/gsap";
import { onMount, onView, onTrack, onDestroy, onPageIn, onPageOut } from "./_";

export default function (element) {
  const blocks = element.querySelectorAll(".cf_block");
  const count = blocks.length;
  const startAngle = (-5 * Math.PI) / 6;
  const endAngle = -Math.PI / 6 - 2 * Math.PI;
  const angleStep = (endAngle - startAngle) / (count - 1);
  const clampedWidth = Math.min(window.innerWidth, 1360);
  const radiusX = clampedWidth * 0.3819444444;
  const radiusY = clampedWidth * 0.1736111111;
  const magnetMax = 10;
  const flipThreshold = -50;

  const positions = Array.from({ length: count }, (_, i) => ({
    x: radiusX * Math.cos(startAngle + angleStep * i),
    y: radiusY * Math.sin(startAngle + angleStep * i),
  }));

  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  Object.assign(svg.style, {
    position: "absolute",
    inset: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    overflow: "visible",
  });
  element.style.position ||= "relative";
  element.prepend(svg);

  const lines = positions.map(({ x, y }) => {
    const line = document.createElementNS(ns, "line");
    line.setAttribute("x1", "50%");
    line.setAttribute("y1", "50%");
    line.setAttribute("stroke", "currentColor");
    line.setAttribute("stroke-width", "1");
    line.setAttribute("stroke-dasharray", "4 4");
    line.setAttribute("stroke-opacity", "0.3");
    svg.appendChild(line);
    return line;
  });

  function updateLines(offsets) {
    const rect = element.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    lines.forEach((line, i) => {
      const ox = offsets ? offsets[i].x : 0;
      const oy = offsets ? offsets[i].y : 0;
      const blockW = blocks[i].offsetWidth;
      const edgeOffset =
        positions[i].x > flipThreshold ? -(blockW / 2) : blockW / 2;
      line.setAttribute("x2", cx + positions[i].x + ox + edgeOffset);
      line.setAttribute("y2", cy + positions[i].y + oy);
    });
  }

  // -- Flip layout for blocks on the right side
  blocks.forEach((block, i) => {
    if (positions[i].x > flipThreshold) {
      block.style.display = "flex";
      block.style.flexDirection = "row";
    }
  });

  // -- Animate blocks out to their positions
  gsap.to(blocks, {
    x: (i) => positions[i].x,
    y: (i) => positions[i].y,
    duration: 0.8,
    stagger: 0.05,
    ease: "power3.out",
    onUpdate() {
      // Sync lines with in-progress animation
      const offsets = Array.from(blocks).map((b) => ({
        x:
          gsap.getProperty(b, "x") - positions[Array.from(blocks).indexOf(b)].x,
        y:
          gsap.getProperty(b, "y") - positions[Array.from(blocks).indexOf(b)].y,
      }));
      updateLines(offsets);
    },
    onComplete: () => updateLines(null),
  });

  // -- Mouse magnetism (max ±20px per block)
  const magOffsets = positions.map(() => ({ x: 0, y: 0 }));

  function onMouseMove(e) {
    const rect = element.getBoundingClientRect();
    const mx = e.clientX - rect.left - rect.width / 2;
    const my = e.clientY - rect.top - rect.height / 2;

    blocks.forEach((block, i) => {
      const dx = mx - positions[i].x;
      const dy = my - positions[i].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const pull = Math.min(magnetMax, magnetMax * (200 / (dist + 200)));
      const angle = Math.atan2(dy, dx);
      const ox = pull * Math.cos(angle);
      const oy = pull * Math.sin(angle);
      magOffsets[i] = { x: ox, y: oy };

      gsap.to(block, {
        x: positions[i].x + ox,
        y: positions[i].y + oy,
        duration: 0.6,
        ease: "power2.out",
        overwrite: "auto",
      });
    });

    updateLines(magOffsets);
  }

  function onMouseLeave() {
    blocks.forEach((block, i) => {
      magOffsets[i] = { x: 0, y: 0 };
      gsap.to(block, {
        x: positions[i].x,
        y: positions[i].y,
        duration: 0.6,
        ease: "power2.out",
        overwrite: "auto",
      });
    });
    updateLines(null);
  }

  element.addEventListener("mousemove", onMouseMove);
  element.addEventListener("mouseleave", onMouseLeave);

  onDestroy(() => {
    element.removeEventListener("mousemove", onMouseMove);
    element.removeEventListener("mouseleave", onMouseLeave);
    svg.remove();
  });
}
